import type {DiffAnchor} from '@github-ui/diffs/types'
import {memo, type RefObject, useMemo} from 'react'

import {getDiffHunksData} from '../helpers/hunk-data-helpers'
import {isEmptyDiffLine} from '../helpers/line-helpers'
import {useGridNavigation} from '../hooks/use-grid-navigation'
import type {DiffLine, ClientDiffLine, FileDiffReference} from '../types'
import {EMPTY_DIFF_LINE} from '../types'
import SplitDiffRow from './SplitDiffRow'
import {useDiffLinesActions, useGetFirstLineSelectionOnDrag} from '../stores/selectors'

/**
 * Split a context line into two lines, one for each side of the diff. The only divergence will be the annotations and
 * threads data, which will be filtered to only include threads and annotations on the right side.
 */
function splitContextLine(line: DiffLine): [DiffLine, DiffLine] {
  const leftLine: DiffLine = {
    ...line,
    threadsData: undefined,
    annotationsData: undefined,
  }
  const rightLine: DiffLine = {
    ...line,
    threadsData: line.threadsData
      ? {
          ...line.threadsData,
          threads: line.threadsData.threads,
          totalCommentsCount: line.threadsData.totalCommentsCount,
          totalCount: line.threadsData.totalCount,
        }
      : undefined,
  }
  return [leftLine, rightLine]
}

function groupDiffLines(lines: DiffLine[]): {
  leftLines: ClientDiffLine[]
  rightLines: ClientDiffLine[]
} {
  const leftLines: ClientDiffLine[] = []
  const rightLines: ClientDiffLine[] = []

  // Keep list of lines even, filling in blanks as needed
  const fillBlanks = () => {
    while (leftLines.length < rightLines.length) {
      leftLines.push(EMPTY_DIFF_LINE)
    }

    while (rightLines.length < leftLines.length) {
      rightLines.push(EMPTY_DIFF_LINE)
    }
  }

  for (const line of lines) {
    switch (line.type) {
      case 'ADDITION':
        rightLines.push(line)
        break
      case 'DELETION':
        leftLines.push(line)
        break
      case 'CONTEXT':
      case 'INJECTED_CONTEXT':
        fillBlanks()
        // eslint-disable-next-line no-case-declarations
        const [leftLine, rightLine] = splitContextLine(line)
        leftLines.push(leftLine)
        rightLines.push(rightLine)
        break
      case 'HUNK':
        fillBlanks()
        leftLines.push(line)
        rightLines.push(line)
    }
  }
  fillBlanks()

  return {leftLines, rightLines}
}

function useGroupedLines(diffLines: DiffLine[]) {
  const commentCount = diffLines.reduce((acc, line) => acc + (line.threadsData?.totalCount ?? 0), 0) ?? 0

  // This useMemo prevents async loaded comments from being injected into split diff lines if you're not using GQL
  // to get around this without losing memo, we check if the comment count has changed and use it as a dependency for the memo
  // eslint-disable-next-line react-hooks/react-compiler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const {leftLines, rightLines} = useMemo(() => groupDiffLines(diffLines), [diffLines, commentCount])

  return {leftLines, rightLines}
}

const SplitDiffLines = memo(function SplitDiffLines({
  diffHasHiddenUnicodeCharacters,
  diffLines,
  fileAnchor,
  filePath,
  handleDiffSideCellSelectionBlocking,
  showHiddenUnicode,
  tableRef,
  copilotChatReference,
}: {
  diffHasHiddenUnicodeCharacters: boolean
  diffLines: DiffLine[]
  fileAnchor: DiffAnchor
  fileLineCount: number
  filePath: string
  handleDiffSideCellSelectionBlocking: (diffSide: 'left' | 'right') => void
  showHiddenUnicode: boolean
  tableRef: RefObject<HTMLTableElement>
  copilotChatReference?: FileDiffReference
}) {
  const {leftLines, rightLines} = useGroupedLines(diffLines)
  const hunksData = useMemo(() => getDiffHunksData(leftLines), [leftLines])
  const {addLineToSelection, clearSelectedLines, selectMultipleLines} = useDiffLinesActions()
  const getfirstLineSelectionOnDrag = useGetFirstLineSelectionOnDrag()

  useGridNavigation({
    addLineToSelection,
    clearSelectedLines,
    containerRef: tableRef,
    fileAnchor,
    getfirstLineSelectionOnDrag,
    isSplitDiff: true,
    leftLines,
    selectMultipleLines,
    rightLines,
    disabled: false,
  })

  return (
    <>
      {leftLines.map((_, index) => {
        const leftDiffLine = leftLines[index] as ClientDiffLine
        const rightDiffLine = rightLines[index] as ClientDiffLine
        const prevDiffLine = leftLines[index - 1]
        const nextDiffLine = isEmptyDiffLine(leftLines[index + 1]) ? rightLines[index + 1] : leftLines[index + 1]

        const key = `${
          isEmptyDiffLine(leftDiffLine) || !leftDiffLine ? `empty-left-diff-${index}` : leftDiffLine.blobLineNumber
        }-${
          isEmptyDiffLine(rightDiffLine) || !rightDiffLine ? `empty-right-diff-${index}` : rightDiffLine.blobLineNumber
        }`

        return (
          <SplitDiffRow
            copilotChatReference={copilotChatReference}
            diffHasHiddenUnicodeCharacters={diffHasHiddenUnicodeCharacters}
            filePath={filePath}
            handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
            hunksData={hunksData}
            key={key}
            leftDiffLine={leftDiffLine}
            nextDiffLine={nextDiffLine}
            prevDiffLine={prevDiffLine}
            rightDiffLine={rightDiffLine}
            showHiddenUnicode={showHiddenUnicode}
          />
        )
      })}
    </>
  )
})

export default SplitDiffLines

try{ SplitDiffLines.displayName ||= 'SplitDiffLines' } catch {}